import Dexie, { type EntityTable } from 'dexie'
if (import.meta.client) {
  await import('dexie-observable')
}
import { defineStore } from 'pinia'

export interface WorkspaceSessionEntry {
  workspaceId: string
  sessionKey: string
  userId: string
  name: string
  color: string
  lastLogin: number
}

export interface WorkspaceKeyEventListener {
  updated(key: string)
}

const DEFAULT_CONF_KEY = '__DEFAULT__'

export const useWorkspaceSessionStore = defineStore(
  'workspaces-session',
  () => {

    const listeners: WorkspaceKeyEventListener[] = []

    const addListener = (l: WorkspaceKeyEventListener) => {
      listeners.push(l)
    }

    const removeListener = (l: WorkspaceKeyEventListener) => {
      removeFromArray(listeners, l)
    }

    if (import.meta.client) {
      const db = new Dexie('jps-workspace-key-database') as Dexie & {
        workspaceSessions: EntityTable<WorkspaceSessionEntry, 'workspaceId'>
      }
      db.version(1).stores({
        workspaceSessions: '&workspaceId',
      })
      db.on('changes', async (changes) => {
        changes.forEach((change) => {
          listeners.forEach((l) => l.updated(change.key))
        })
      })

      const setDefaultConf = async (name: string, color: string) => {
        await db.workspaceSessions.update(DEFAULT_CONF_KEY, {name, color})
      }

      const getDefaultConf = async () => {
        return db.workspaceSessions.get(DEFAULT_CONF_KEY)
      }

      const getSession = async (workspaceId: string) => {
        return db.workspaceSessions.get(workspaceId)
      }

      const createSession = async (
        workspaceId: string,
        sessionKey: string,
        userId: string,
        name: string,
        color: string
      ) => {
        await db.workspaceSessions.add({
          workspaceId: workspaceId,
          sessionKey: sessionKey,
          userId: userId,
          name: name,
          color: color,
          lastLogin: now(),
        })
      }

      const updateSession = async (
        workspaceId: string,
        update: {
          sessionKey?: string
          userId?: string
          name?: string
          color?: string
        }
      ) => {
        await db.workspaceSessions.update(workspaceId, {
          ...update,
          lastLogin: now(),
        })
      }

      const deleteSession = async (workspaceId: string) => {
        await db.workspaceSessions.delete(workspaceId)
      }

      return {
        addListener,
        removeListener,
        setDefaultConf,
        getDefaultConf,
        getSession,
        createSession,
        updateSession,
        deleteSession,
      }

    } else {

      const setDefaultConf = async (name: string, color: string) => {}
      const getDefaultConf = async () => { return null }
      const getSession = async (workspaceId: string) => { return null }
      const createSession = async (
        workspaceId: string,
        sessionKey: string,
        userId: string,
        name: string,
        color: string
      ) => {}
      const updateSession = async (
        workspaceId: string,
        update: {
          sessionKey?: string
          userId?: string
          name?: string
          color?: string
        }
      ) => {}
      const deleteSession = async (workspaceId: string) => {}

      return {
        addListener,
        removeListener,
        setDefaultConf,
        getDefaultConf,
        getSession,
        createSession,
        updateSession,
        deleteSession,
      }

    }

  },
  { persist: false },
)
